import { useMemo, useState } from 'react';
import { Lock, Users, Building2, Trash2, Save } from 'lucide-react';
import { Button } from '@/shared/components/ui/Button';
import { Badge } from '@/shared/components/ui/Badge';
import { Spinner } from '@/shared/components/ui/Spinner';
import { useUser } from '@/shared/hooks/useAuth';
import { useUserOptions } from '@/shared/hooks/useUserOptions';
import { cn } from '@/shared/utils/utils';
import { ACCESS_OPTIONS, accessMeta, prettyDept } from '../constants';
import type { AccessControl, Note, SaveNotePayload } from '../types';

const ACCESS_ICON = { private: Lock, user: Users, department: Building2 } as const;

interface NotePropertiesPanelProps {
  note: Note;
  canManage: boolean;
  saving: boolean;
  deleting: boolean;
  onSave: (id: string, payload: SaveNotePayload) => void;
  onDelete: (note: Note) => void;
}

function fmtDate(iso?: string) {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleString('en-IN', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

/** Right column — note properties + the real sharing/access editor. */
export function NotePropertiesPanel({ note, canManage, saving, deleting, onSave, onDelete }: NotePropertiesPanelProps) {
  const me = useUser();
  const { data: users = [], isLoading: usersLoading } = useUserOptions(canManage);

  const [access, setAccess] = useState<AccessControl>(note.access_control);
  const [userIds, setUserIds] = useState<string[]>(note.shared_with_users ?? []);
  const [depts, setDepts] = useState<string[]>(note.shared_with_depts ?? []);

  // Reset local sharing draft when the selected note changes.
  const noteKey = `${note.id}:${note.access_control}`;
  const [seenKey, setSeenKey] = useState(noteKey);
  if (seenKey !== noteKey) {
    setSeenKey(noteKey);
    setAccess(note.access_control);
    setUserIds(note.shared_with_users ?? []);
    setDepts(note.shared_with_depts ?? []);
  }

  const assignableUsers = useMemo(() => users.filter((u) => u.id !== me?.id), [users, me?.id]);
  const departmentOptions = useMemo(() => {
    const set = new Set<string>();
    users.forEach((u) => u.department && set.add(u.department));
    if (me?.department) set.add(me.department);
    return [...set].sort();
  }, [users, me?.department]);

  const toggle = (list: string[], value: string) =>
    list.includes(value) ? list.filter((v) => v !== value) : [...list, value];

  const dirty =
    access !== note.access_control ||
    JSON.stringify([...userIds].sort()) !== JSON.stringify([...(note.shared_with_users ?? [])].sort()) ||
    JSON.stringify([...depts].sort()) !== JSON.stringify([...(note.shared_with_depts ?? [])].sort());

  const valid = access !== 'user' ? (access !== 'department' || depts.length > 0) : userIds.length > 0;

  const handleSave = () => {
    if (!valid || !dirty) return;
    onSave(note.id, {
      title: note.title,
      notebookText: note.notebookText,
      access_control: access,
      shared_with_users: access === 'user' ? userIds : [],
      shared_with_depts: access === 'department' ? depts : [],
    });
  };

  return (
    <aside className="flex h-full w-72 shrink-0 flex-col border-l border-outline bg-surface-container-low">
      <div className="border-b border-outline px-4 py-3">
        <h2 className="text-data-lg font-semibold text-on-surface">Properties</h2>
      </div>

      <div className="flex-1 space-y-5 overflow-y-auto p-4">
        {/* Metadata */}
        <Section title="Details">
          <Meta label="Owner" value={note.author_name || '—'} />
          <Meta label="Created" value={fmtDate(note.created_at)} />
          <Meta label="Updated" value={fmtDate(note.updated_at)} />
        </Section>

        {/* Sharing / access */}
        <Section title="Sharing">
          {!canManage ? (
            <div className="flex items-center gap-2">
              <Badge tone={accessMeta(note.access_control).tone}>{accessMeta(note.access_control).label}</Badge>
              <span className="text-body-sm text-on-surface-variant">Read-only</span>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-2">
                {ACCESS_OPTIONS.map((opt) => {
                  const Icon = ACCESS_ICON[opt.value];
                  const active = access === opt.value;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setAccess(opt.value)}
                      className={cn(
                        'flex flex-col items-center gap-1 rounded-card-sm border px-2 py-2 text-center transition-colors',
                        active
                          ? 'border-primary bg-primary-container/50 text-on-surface'
                          : 'border-outline text-on-surface-variant hover:bg-surface-variant',
                      )}
                      aria-pressed={active}
                    >
                      <Icon className="h-4 w-4" />
                      <span className="text-[11px] font-medium">{opt.label}</span>
                    </button>
                  );
                })}
              </div>
              <p className="text-body-sm text-on-surface-variant">{accessMeta(access).description}</p>

              {access === 'user' && (
                <div>
                  {usersLoading ? (
                    <div className="flex justify-center py-3"><Spinner /></div>
                  ) : assignableUsers.length === 0 ? (
                    <p className="text-body-sm text-on-surface-variant">No other users available.</p>
                  ) : (
                    <div className="max-h-44 space-y-1 overflow-y-auto rounded-card-sm border border-outline p-2">
                      {assignableUsers.map((u) => (
                        <label key={u.id} className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 hover:bg-surface-variant">
                          <input
                            type="checkbox"
                            checked={userIds.includes(u.id)}
                            onChange={() => setUserIds((l) => toggle(l, u.id))}
                            className="accent-primary"
                          />
                          <span className="truncate text-data-md text-on-surface">{u.display_name}</span>
                        </label>
                      ))}
                    </div>
                  )}
                  {userIds.length === 0 && <p className="mt-1 text-body-sm text-danger">Select at least one user.</p>}
                </div>
              )}

              {access === 'department' && (
                <div>
                  {departmentOptions.length === 0 ? (
                    <p className="text-body-sm text-on-surface-variant">No departments available.</p>
                  ) : (
                    <div className="flex flex-wrap gap-1.5">
                      {departmentOptions.map((d) => {
                        const active = depts.includes(d);
                        return (
                          <button
                            key={d}
                            type="button"
                            onClick={() => setDepts((l) => toggle(l, d))}
                            className={cn(
                              'rounded-full border px-2.5 py-1 text-body-sm transition-colors',
                              active
                                ? 'border-primary bg-primary-container/60 text-on-surface'
                                : 'border-outline text-on-surface-variant hover:bg-surface-variant',
                            )}
                            aria-pressed={active}
                          >
                            {prettyDept(d)}
                          </button>
                        );
                      })}
                    </div>
                  )}
                  {depts.length === 0 && <p className="mt-1 text-body-sm text-danger">Select at least one department.</p>}
                </div>
              )}

              <Button size="sm" className="w-full justify-center" loading={saving} disabled={!dirty || !valid} onClick={handleSave}>
                <Save className="h-3.5 w-3.5" /> Save Sharing
              </Button>
            </div>
          )}
        </Section>
      </div>

      {canManage && (
        <div className="border-t border-outline p-4">
          <Button variant="danger" size="sm" className="w-full justify-center" loading={deleting} onClick={() => onDelete(note)}>
            <Trash2 className="h-3.5 w-3.5" /> Delete Note
          </Button>
        </div>
      )}
    </aside>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="space-y-2">
      <p className="text-label-caps uppercase text-on-surface-variant">{title}</p>
      {children}
    </div>
  );
}

function Meta({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-3 text-body-sm">
      <span className="text-on-surface-variant">{label}</span>
      <span className="text-right text-on-surface">{value}</span>
    </div>
  );
}
